import * as React from 'react';
import { useTranslate, useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const statusColor = (status: string) => {
    switch (status?.toUpperCase()) {
        case 'ACTIVE':
            return 'success';
        case 'DRAINING':
            return 'warning';
        case 'INACTIVE':
            return 'error';
        default:
            return 'default';
    }
};

const EcsServicesPanel = () => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin/ecs-services'],
        queryFn: () => dataProvider.getAny('admin/ecs-services', ''),
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const services = data?.data || [];

    return (
        <Card>
            <CardHeader
                title={translate('hallinta.ecs.title')}
                action={
                    <Tooltip title={translate('ra.action.refresh')}>
                        <IconButton onClick={() => refetch()} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                }
            />
            <CardContent>
                {isLoading && <CircularProgress size={24} />}
                {error && (
                    <Alert severity="error">
                        {translate('hallinta.error')}:{' '}
                        {(error as Error).message}
                    </Alert>
                )}
                {!isLoading && !error && services.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                        {translate('hallinta.ecs.no_services')}
                    </Typography>
                )}
                {services.length > 0 && (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {translate(
                                            'hallinta.ecs.fields.serviceName'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.ecs.fields.status'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {translate(
                                            'hallinta.ecs.fields.running'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {translate(
                                            'hallinta.ecs.fields.desired'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {translate(
                                            'hallinta.ecs.fields.pending'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.ecs.fields.taskDefinition'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            'hallinta.ecs.fields.lastEvent'
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {services.map((svc: any) => (
                                    <TableRow key={svc.id}>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                            >
                                                {svc.serviceName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={svc.status}
                                                color={
                                                    statusColor(
                                                        svc.status
                                                    ) as any
                                                }
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                color={
                                                    svc.runningCount ===
                                                    svc.desiredCount
                                                        ? 'success.main'
                                                        : 'warning.main'
                                                }
                                                fontWeight="bold"
                                            >
                                                {svc.runningCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {svc.desiredCount}
                                        </TableCell>
                                        <TableCell align="center">
                                            {svc.pendingCount > 0 ? (
                                                <Chip
                                                    label={svc.pendingCount}
                                                    color="warning"
                                                    size="small"
                                                />
                                            ) : (
                                                svc.pendingCount
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace' }}
                                            >
                                                {svc.taskDefinition}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {svc.lastEvent && (
                                                <Tooltip
                                                    title={
                                                        svc.lastEvent.message
                                                    }
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        sx={{
                                                            maxWidth: 200,
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {svc.lastEvent.message}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default EcsServicesPanel;
